import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min";
import "bootstrap-icons/font/bootstrap-icons.css";
import { apiClient } from "../../services/setupClientSubmaster/apiCliente";
import LocalStorage from "../../helpers/localstorage";
import DeliveryOptionsTable from "./deliveryOptionsTable";
import WebUserManagementPanel from "./webUserManagementPanel";

type Submaster = {
  id: number;
  cnpj: string;
  email: string;
  name?: string | null;
  fantasy_name?: string | null;
  updatedAt?: string;
  delete_permission_enabled?: boolean | null;
  email_permission_enabled?: boolean | null;
  report_epi_permission_enabled?: boolean | null;
  report_top_largest_permission_enabled?: boolean | null;
  report_top_smallest_permission_enabled?: boolean | null;
  report_latest_by_employee_permission_enabled?: boolean | null;
  report_employee_cost_permission_enabled?: boolean | null;
  report_epi_output_cost_permission_enabled?: boolean | null;
  report_epi_output_employee_cost_permission_enabled?: boolean | null;
  report_epi_output_grouped_employee_cost_permission_enabled?: boolean | null;
  report_individual_photo_by_epi_permission_enabled?: boolean | null;
  report_low_stock_permission_enabled?: boolean | null;
  report_epi_output_permission_enabled?: boolean | null;
  report_epi_retirada_permission_enabled?: boolean | null;
  report_employee_withdrawal_permission_enabled?: boolean | null;
  report_top_employee_withdrawal_permission_enabled?: boolean | null;
  report_employees_permission_enabled?: boolean | null;
  report_excel_permission_enabled?: boolean | null;
  stock_control_permission_enabled?: boolean | null;
  retirar_foto_entrega_permission_enabled?: boolean | null;
  camera_traseira_permission_enabled?: boolean | null;
  company_delete_permission_enabled?: boolean | null;
  delivery_type_reason_permission_enabled?: boolean | null;
  employee_signature_photo_permission_enabled?: boolean | null;
  fixed_report_signature_permission_enabled?: boolean | null;
  master_data_delete_permission_enabled?: boolean | null;
};

type BaseReportType =
  | "epi-report"
  | "top-largest"
  | "top-smallest"
  | "latest-by-employee"
  | "cost-by-employee"
  | "epi-output-cost-report"
  | "epi-output-employee-cost-report"
  | "epi-output-grouped-employee-cost-report"
  | "individual-photo-by-epi";

const baseReportPermissionFields: Record<BaseReportType, keyof Submaster> = {
  "epi-report": "report_epi_permission_enabled",
  "top-largest": "report_top_largest_permission_enabled",
  "top-smallest": "report_top_smallest_permission_enabled",
  "latest-by-employee": "report_latest_by_employee_permission_enabled",
  "cost-by-employee": "report_employee_cost_permission_enabled",
  "epi-output-cost-report": "report_epi_output_cost_permission_enabled",
  "epi-output-employee-cost-report": "report_epi_output_employee_cost_permission_enabled",
  "epi-output-grouped-employee-cost-report": "report_epi_output_grouped_employee_cost_permission_enabled",
  "individual-photo-by-epi": "report_individual_photo_by_epi_permission_enabled",
};

const ACCESS_STORAGE_KEY = "data_submaster_internal_access";
const INTERNAL_ACCESS_KEY = "chavedeacessomaster#2528";

const dataTableStyles = {
  headRow: {
    style: {
      minHeight: "54px",
      backgroundColor: "#f4f8ff",
      borderBottom: "1px solid rgba(13, 110, 253, 0.1)",
      fontSize: "0.9rem",
      fontWeight: 700,
      color: "#17324d",
    },
  },
  rows: {
    style: {
      minHeight: "66px",
      fontSize: "0.94rem",
      color: "#29445c",
      cursor: "pointer",
      "&:not(:last-of-type)": {
        borderBottom: "1px solid rgba(18, 49, 58, 0.08)",
      },
    },
  },
  cells: {
    style: {
      paddingTop: "10px",
      paddingBottom: "10px",
    },
  },
  pagination: {
    style: {
      borderTop: "1px solid rgba(18, 49, 58, 0.08)",
    },
  },
};

const formatCNPJ = (value: string): string => {
  const onlyNumbers = value.replace(/\D/g, "");
  if (onlyNumbers.length === 14) {
    return onlyNumbers.replace(
      /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
      "$1.$2.$3/$4-$5"
    );
  }

  return value || "—";
};

const formatDate = (dateStr?: string): string => {
  if (!dateStr) return "—";
  const date = new Date(dateStr);
  return date.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getCompanyName = (item: Submaster) =>
  item.fantasy_name?.trim() || item.name?.trim() || "Empresa sem nome";

const isDeletePermissionEnabled = (item: Submaster) =>
  item.delete_permission_enabled !== false;

const isCompanyDeletePermissionEnabled = (item: Submaster) =>
  item.company_delete_permission_enabled === true;

const isEmailPermissionEnabled = (item: Submaster) =>
  item.email_permission_enabled !== false;

const isBaseReportPermissionEnabled = (item: Submaster, reportType: BaseReportType) =>
  reportType === "cost-by-employee" ||
  reportType === "epi-output-cost-report" ||
  reportType === "epi-output-employee-cost-report" ||
  reportType === "epi-output-grouped-employee-cost-report" ||
  reportType === "individual-photo-by-epi"
    ? item[baseReportPermissionFields[reportType]] === true
    : item[baseReportPermissionFields[reportType]] !== false;

const isReportLowStockPermissionEnabled = (item: Submaster) =>
  item.report_low_stock_permission_enabled === true;

const isEpiOutputReportPermissionEnabled = (item: Submaster) =>
  item.report_epi_output_permission_enabled === true;

const isReportEpiRetiradaPermissionEnabled = (item: Submaster) =>
  item.report_epi_retirada_permission_enabled === true;

const isReportEmployeeWithdrawalPermissionEnabled = (item: Submaster) =>
  item.report_employee_withdrawal_permission_enabled === true;

const isReportTopEmployeeWithdrawalPermissionEnabled = (item: Submaster) =>
  item.report_top_employee_withdrawal_permission_enabled === true;

const isReportEmployeesPermissionEnabled = (item: Submaster) =>
  item.report_employees_permission_enabled === true;

const isReportExcelPermissionEnabled = (item: Submaster) =>
  item.report_excel_permission_enabled === true;

const isStockControlPermissionEnabled = (item: Submaster) =>
  item.stock_control_permission_enabled === true;

const isRetirarFotoEntregaPermissionEnabled = (item: Submaster) =>
  item.retirar_foto_entrega_permission_enabled === true;

const isCameraTraseiraPermissionEnabled = (item: Submaster) =>
  item.camera_traseira_permission_enabled === true;

const isDeliveryTypeReasonPermissionEnabled = (item: Submaster) =>
  item.delivery_type_reason_permission_enabled === true;

const isEmployeeSignaturePhotoPermissionEnabled = (item: Submaster) =>
  item.employee_signature_photo_permission_enabled === true;

const isFixedReportSignaturePermissionEnabled = (item: Submaster) =>
  item.fixed_report_signature_permission_enabled === true;

const isMasterDataDeletePermissionEnabled = (item: Submaster) =>
  item.master_data_delete_permission_enabled === true;

const sortSubmastersAlphabetically = (items: Submaster[]) =>
  [...items].sort((a, b) => {
    const companyComparison = getCompanyName(a).localeCompare(getCompanyName(b), "pt-BR", {
      sensitivity: "base",
    });

    return companyComparison || formatCNPJ(a.cnpj).localeCompare(formatCNPJ(b.cnpj), "pt-BR");
  });

function DataSubmasterPage() {
  const [data, setData] = useState<Submaster[]>([]);
  const [filteredData, setFilteredData] = useState<Submaster[]>([]);
  const [loading, setLoading] = useState(false);
  const [accessKey, setAccessKey] = useState("");
  const [authenticated, setAuthenticated] = useState(false);
  const [filterCompany, setFilterCompany] = useState("");
  const [filterCNPJ, setFilterCNPJ] = useState("");
  const [expandedCompanyId, setExpandedCompanyId] = useState<number | null>(null);
  const [permissionLoadingId, setPermissionLoadingId] = useState<number | null>(null);
  const [companyDeletePermissionLoadingId, setCompanyDeletePermissionLoadingId] = useState<number | null>(null);
  const [emailPermissionLoadingId, setEmailPermissionLoadingId] = useState<number | null>(null);
  const [baseReportPermissionLoadingKey, setBaseReportPermissionLoadingKey] = useState<string | null>(null);
  const [reportLowStockPermissionLoadingId, setReportLowStockPermissionLoadingId] = useState<number | null>(null);
  const [epiOutputReportPermissionLoadingId, setEpiOutputReportPermissionLoadingId] = useState<number | null>(null);
  const [reportEpiRetiradaPermissionLoadingId, setReportEpiRetiradaPermissionLoadingId] = useState<number | null>(null);
  const [reportEmployeeWithdrawalPermissionLoadingId, setReportEmployeeWithdrawalPermissionLoadingId] = useState<number | null>(null);
  const [reportTopEmployeeWithdrawalPermissionLoadingId, setReportTopEmployeeWithdrawalPermissionLoadingId] = useState<number | null>(null);
  const [reportEmployeesPermissionLoadingId, setReportEmployeesPermissionLoadingId] = useState<number | null>(null);
  const [reportExcelPermissionLoadingId, setReportExcelPermissionLoadingId] = useState<number | null>(null);
  const [stockControlPermissionLoadingId, setStockControlPermissionLoadingId] = useState<number | null>(null);
  const [retirarFotoPermissionLoadingId, setRetirarFotoPermissionLoadingId] = useState<number | null>(null);
  const [cameraTraseiraPermissionLoadingId, setCameraTraseiraPermissionLoadingId] = useState<number | null>(null);
  const [deliveryTypeReasonPermissionLoadingId, setDeliveryTypeReasonPermissionLoadingId] = useState<number | null>(null);
  const [employeeSignaturePhotoPermissionLoadingId, setEmployeeSignaturePhotoPermissionLoadingId] = useState<number | null>(null);
  const [fixedReportSignaturePermissionLoadingId, setFixedReportSignaturePermissionLoadingId] = useState<number | null>(null);
  const [masterDataDeletePermissionLoadingId, setMasterDataDeletePermissionLoadingId] = useState<number | null>(null);
  const [deleteLoadingId, setDeleteLoadingId] = useState<number | null>(null);
  const [accessCompanyLoadingId, setAccessCompanyLoadingId] = useState<number | null>(null);

  const getInternalPayload = () => ({
    accessKey: INTERNAL_ACCESS_KEY,
  });

  const handleGetDataSubmaster = useCallback(async () => {
    try {
      setLoading(true);
      const ApiClient = apiClient();
      const response = await ApiClient.post(`/getSubmaster`, getInternalPayload());
      const sortedData = sortSubmastersAlphabetically(response.data || []);
      setData(sortedData);
      setFilteredData(sortedData);
    } catch (error) {
      console.error("Erro ao buscar submasters:", error);
      alert("Não foi possível carregar as empresas internas.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (window.localStorage.getItem(ACCESS_STORAGE_KEY) === "granted") {
      setAuthenticated(true);
    }
  }, []);

  useEffect(() => {
    if (authenticated) {
      handleGetDataSubmaster();
    }
  }, [authenticated, handleGetDataSubmaster]);

  const handleAccess = () => {
    if (accessKey === INTERNAL_ACCESS_KEY) {
      window.localStorage.setItem(ACCESS_STORAGE_KEY, "granted");
      setAuthenticated(true);
      return;
    }

    alert("Chave inválida. Tente novamente.");
  };

  const handleLogout = () => {
    window.localStorage.removeItem(ACCESS_STORAGE_KEY);
    setAuthenticated(false);
    setAccessKey("");
  };

  const clearFilters = () => {
    setFilterCompany("");
    setFilterCNPJ("");
  };

  useEffect(() => {
    let filtered = data;

    if (filterCompany.trim() !== "") {
      filtered = filtered.filter((item) =>
        getCompanyName(item).toLowerCase().includes(filterCompany.toLowerCase())
      );
    }

    if (filterCNPJ.trim() !== "") {
      filtered = filtered.filter((item) =>
        formatCNPJ(item.cnpj).toLowerCase().includes(filterCNPJ.toLowerCase())
      );
    }

    setFilteredData(sortSubmastersAlphabetically(filtered));
  }, [data, filterCompany, filterCNPJ]);

  const copyCNPJ = async (cnpj: string) => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(cnpj);
      } else {
        const tempInput = document.createElement("input");
        tempInput.value = cnpj;
        document.body.appendChild(tempInput);
        tempInput.select();
        document.execCommand("copy");
        document.body.removeChild(tempInput);
      }

      alert(`CNPJ copiado: ${formatCNPJ(cnpj)}`);
    } catch (error) {
      console.error("Erro ao copiar CNPJ:", error);
      alert("Não foi possível copiar o CNPJ.");
    }
  };

  const handleToggleDeletePermission = async (row: Submaster) => {
    if (permissionLoadingId === row.id || !row.id) {
      return;
    }

    const nextValue = !isDeletePermissionEnabled(row);
    const actionLabel = nextValue ? "ativar" : "bloquear";
    const confirmed = window.confirm(
      `Deseja ${actionLabel} a permissão de exclusão da empresa ${getCompanyName(row)}?\n\n` +
        "Essa regra controla a exclusão permanente nos relatórios da empresa."
    );

    if (!confirmed) {
      return;
    }

    try {
      setPermissionLoadingId(row.id);
      const ApiClient = apiClient();
      const response = await ApiClient.post(`/internal/updateDeletePermission`, {
        ...getInternalPayload(),
        submasterId: row.id,
        enabled: nextValue,
      });

      if (!response?.data?.res) {
        throw new Error(response?.data?.message || "Não foi possível atualizar a permissão.");
      }

      setData((previous) =>
        previous.map((item) =>
          item.id === row.id
            ? { ...item, delete_permission_enabled: nextValue }
            : item
        )
      );
    } catch (error) {
      console.error("Erro ao atualizar permissão:", error);
      alert("Não foi possível atualizar a permissão de exclusão.");
    } finally {
      setPermissionLoadingId(null);
    }
  };

  const handleToggleMasterDataDeletePermission = async (row: Submaster) => {
    if (masterDataDeletePermissionLoadingId === row.id || !row.id) {
      return;
    }

    const nextValue = !isMasterDataDeletePermissionEnabled(row);
    const actionLabel = nextValue ? "ativar" : "bloquear";
    const confirmed = window.confirm(
      `Deseja ${actionLabel} a permissão para excluir funcionário, usuário e EPI da empresa ${getCompanyName(row)}?\n\n` +
        "Mesmo com a permissão ativa, registros que já participaram de uma entrega não poderão ser apagados."
    );

    if (!confirmed) {
      return;
    }

    try {
      setMasterDataDeletePermissionLoadingId(row.id);
      const ApiClient = apiClient();
      const response = await ApiClient.post(`/internal/updateMasterDataDeletePermission`, {
        ...getInternalPayload(),
        submasterId: row.id,
        enabled: nextValue,
      });

      if (!response?.data?.res) {
        throw new Error(response?.data?.message || "Não foi possível atualizar a permissão.");
      }

      setData((previous) =>
        previous.map((item) =>
          item.id === row.id
            ? { ...item, master_data_delete_permission_enabled: nextValue }
            : item
        )
      );
    } catch (error) {
      console.error("Erro ao atualizar permissão de exclusão dos cadastros:", error);
      alert("Não foi possível atualizar a permissão para excluir funcionário, usuário e EPI.");
    } finally {
      setMasterDataDeletePermissionLoadingId(null);
    }
  };

  const handleAccessCompany = async (row: Submaster) => {
    if (accessCompanyLoadingId !== null || !row.id) {
      return;
    }

    try {
      setAccessCompanyLoadingId(row.id);
      const ApiClient = apiClient();
      const response = await ApiClient.post(`/internal/accessSubmaster`, {
        ...getInternalPayload(),
        submasterId: row.id,
      });
      const session = response?.data;

      if (!session?.res || !session?.token) {
        throw new Error(session?.message || "Não foi possível acessar a empresa.");
      }

      const storage = new LocalStorage();
      storage.saveAuthToken(
        String(session.token),
        String(session.name || getCompanyName(row)),
        String(session.email || row.email || ""),
        String(session.id || row.id),
        String(session.submaster_logo_url || "")
      );
      window.location.assign("/HomeSubmasterPage");
    } catch (error) {
      console.error("Erro ao acessar empresa:", error);
      alert("Não foi possível acessar a empresa selecionada.");
      setAccessCompanyLoadingId(null);
    }
  };

  const handleToggleCompanyDeletePermission = async (row: Submaster) => {
    if (companyDeletePermissionLoadingId === row.id || !row.id) {
      return;
    }

    const nextValue = !isCompanyDeletePermissionEnabled(row);
    const actionLabel = nextValue ? "ativar" : "bloquear";
    const confirmed = window.confirm(
      `Deseja ${actionLabel} a permissão para excluir empresas cadastradas por ${getCompanyName(row)}?`
    );

    if (!confirmed) {
      return;
    }

    try {
      setCompanyDeletePermissionLoadingId(row.id);
      const ApiClient = apiClient();
      const response = await ApiClient.post(`/internal/updateCompanyDeletePermission`, {
        ...getInternalPayload(),
        submasterId: row.id,
        enabled: nextValue,
      });

      if (!response?.data?.res) {
        throw new Error(response?.data?.message || "Não foi possível atualizar a permissão.");
      }

      setData((previous) =>
        previous.map((item) =>
          item.id === row.id
            ? { ...item, company_delete_permission_enabled: nextValue }
            : item
        )
      );
    } catch (error) {
      console.error("Erro ao atualizar permissão de exclusão de empresa:", error);
      alert("Não foi possível atualizar a permissão de exclusão de empresa.");
    } finally {
      setCompanyDeletePermissionLoadingId(null);
    }
  };

  const handleToggleEmailPermission = async (row: Submaster) => {
    if (emailPermissionLoadingId === row.id || !row.id) {
      return;
    }

    const nextValue = !isEmailPermissionEnabled(row);
    const actionLabel = nextValue ? "ativar" : "bloquear";
    const confirmed = window.confirm(
      `Deseja ${actionLabel} o envio de e-mails de alerta (EPI vencido / estoque mínimo) para a empresa ${getCompanyName(row)}?`
    );

    if (!confirmed) {
      return;
    }

    try {
      setEmailPermissionLoadingId(row.id);
      const ApiClient = apiClient();
      const response = await ApiClient.post(`/internal/updateEmailPermission`, {
        ...getInternalPayload(),
        submasterId: row.id,
        enabled: nextValue,
      });

      if (!response?.data?.res) {
        throw new Error(response?.data?.message || "Não foi possível atualizar a permissão.");
      }

      setData((previous) =>
        previous.map((item) =>
          item.id === row.id
            ? { ...item, email_permission_enabled: nextValue }
            : item
        )
      );
    } catch (error) {
      console.error("Erro ao atualizar permissão de e-mail:", error);
      alert("Não foi possível atualizar a permissão de e-mail.");
    } finally {
      setEmailPermissionLoadingId(null);
    }
  };

  const handleToggleBaseReportPermission = async (
    row: Submaster,
    reportType: BaseReportType,
    reportTitle: string
  ) => {
    if (!row.id) return;

    const loadingKey = `${row.id}:${reportType}`;
    if (baseReportPermissionLoadingKey === loadingKey) return;

    const nextValue = !isBaseReportPermissionEnabled(row, reportType);
    const actionLabel = nextValue ? "ativar" : "bloquear";
    const confirmed = window.confirm(
      `Deseja ${actionLabel} o acesso ao ${reportTitle} para a empresa ${getCompanyName(row)}?`
    );
    if (!confirmed) return;

    try {
      setBaseReportPermissionLoadingKey(loadingKey);
      const ApiClient = apiClient();
      const response = await ApiClient.post(`/internal/updateBaseAdditionalReportPermission`, {
        ...getInternalPayload(),
        submasterId: row.id,
        reportType,
        enabled: nextValue,
      });

      if (!response?.data?.res) {
        throw new Error(response?.data?.message || "Não foi possível atualizar a permissão.");
      }

      const field = baseReportPermissionFields[reportType];
      setData((previous) =>
        previous.map((item) =>
          item.id === row.id ? { ...item, [field]: nextValue } : item
        )
      );
    } catch (error) {
      console.error(`Erro ao atualizar a permissão do relatório ${reportType}:`, error);
      alert("Não foi possível atualizar a permissão do relatório.");
    } finally {
      setBaseReportPermissionLoadingKey(null);
    }
  };

  const handleToggleReportLowStockPermission = async (row: Submaster) => {
    if (reportLowStockPermissionLoadingId === row.id || !row.id) {
      return;
    }

    const nextValue = !isReportLowStockPermissionEnabled(row);
    const actionLabel = nextValue ? "ativar" : "bloquear";
    const confirmed = window.confirm(
      `Deseja ${actionLabel} o acesso ao relatório de todos os EPIs com estoque baixo para a empresa ${getCompanyName(row)}?`
    );

    if (!confirmed) {
      return;
    }

    try {
      setReportLowStockPermissionLoadingId(row.id);
      const ApiClient = apiClient();
      const response = await ApiClient.post(`/internal/updateReportLowStockPermission`, {
        ...getInternalPayload(),
        submasterId: row.id,
        enabled: nextValue,
      });

      if (!response?.data?.res) {
        throw new Error(response?.data?.message || "Não foi possível atualizar a permissão.");
      }

      setData((previous) =>
        previous.map((item) =>
          item.id === row.id
            ? { ...item, report_low_stock_permission_enabled: nextValue }
            : item
        )
      );
    } catch (error) {
      console.error("Erro ao atualizar permissão de relatório de estoque baixo:", error);
      alert("Não foi possível atualizar a permissão de relatório de estoque baixo.");
    } finally {
      setReportLowStockPermissionLoadingId(null);
    }
  };

  const handleToggleEpiOutputReportPermission = async (row: Submaster) => {
    if (epiOutputReportPermissionLoadingId === row.id || !row.id) {
      return;
    }

    const nextValue = !isEpiOutputReportPermissionEnabled(row);
    const actionLabel = nextValue ? "ativar" : "bloquear";
    const confirmed = window.confirm(
      `Deseja ${actionLabel} o acesso ao Relatório de Saída de EPIs para a empresa ${getCompanyName(row)}?`
    );

    if (!confirmed) {
      return;
    }

    try {
      setEpiOutputReportPermissionLoadingId(row.id);
      const ApiClient = apiClient();
      const response = await ApiClient.post(`/internal/updateEpiOutputReportPermission`, {
        ...getInternalPayload(),
        submasterId: row.id,
        enabled: nextValue,
      });

      if (!response?.data?.res) {
        throw new Error(response?.data?.message || "Não foi possível atualizar a permissão.");
      }

      setData((previous) =>
        previous.map((item) =>
          item.id === row.id
            ? { ...item, report_epi_output_permission_enabled: nextValue }
            : item
        )
      );
    } catch (error) {
      console.error("Erro ao atualizar a permissão do Relatório de Saída de EPIs:", error);
      alert("Não foi possível atualizar a permissão do Relatório de Saída de EPIs.");
    } finally {
      setEpiOutputReportPermissionLoadingId(null);
    }
  };

  const handleToggleReportEpiRetiradaPermission = async (row: Submaster) => {
    if (reportEpiRetiradaPermissionLoadingId === row.id || !row.id) {
      return;
    }

    const nextValue = !isReportEpiRetiradaPermissionEnabled(row);
    const actionLabel = nextValue ? "ativar" : "bloquear";
    const confirmed = window.confirm(
      `Deseja ${actionLabel} o acesso ao relatório de retirada de EPI para a empresa ${getCompanyName(row)}?`
    );

    if (!confirmed) {
      return;
    }

    try {
      setReportEpiRetiradaPermissionLoadingId(row.id);
      const ApiClient = apiClient();
      const response = await ApiClient.post(`/internal/updateReportEpiRetiradaPermission`, {
        ...getInternalPayload(),
        submasterId: row.id,
        enabled: nextValue,
      });

      if (!response?.data?.res) {
        throw new Error(response?.data?.message || "Não foi possível atualizar a permissão.");
      }

      setData((previous) =>
        previous.map((item) =>
          item.id === row.id
            ? { ...item, report_epi_retirada_permission_enabled: nextValue }
            : item
        )
      );
    } catch (error) {
      console.error("Erro ao atualizar permissão de relatório de retirada de EPI:", error);
      alert("Não foi possível atualizar a permissão de relatório de retirada de EPI.");
    } finally {
      setReportEpiRetiradaPermissionLoadingId(null);
    }
  };

  const handleToggleReportEmployeeWithdrawalPermission = async (row: Submaster) => {
    if (reportEmployeeWithdrawalPermissionLoadingId === row.id || !row.id) {
      return;
    }

    const nextValue = !isReportEmployeeWithdrawalPermissionEnabled(row);
    const actionLabel = nextValue ? "ativar" : "bloquear";
    const confirmed = window.confirm(
      `Deseja ${actionLabel} o acesso ao relatório de retirada de EPI por funcionário para a empresa ${getCompanyName(row)}?`
    );

    if (!confirmed) {
      return;
    }

    try {
      setReportEmployeeWithdrawalPermissionLoadingId(row.id);
      const ApiClient = apiClient();
      const response = await ApiClient.post(
        `/internal/updateReportEmployeeWithdrawalPermission`,
        {
          ...getInternalPayload(),
          submasterId: row.id,
          enabled: nextValue,
        }
      );

      if (!response?.data?.res) {
        throw new Error(response?.data?.message || "Não foi possível atualizar a permissão.");
      }

      setData((previous) =>
        previous.map((item) =>
          item.id === row.id
            ? { ...item, report_employee_withdrawal_permission_enabled: nextValue }
            : item
        )
      );
    } catch (error) {
      console.error(
        "Erro ao atualizar permissão de relatório de retirada de EPI por funcionário:",
        error
      );
      alert(
        "Não foi possível atualizar a permissão de relatório de retirada de EPI por funcionário."
      );
    } finally {
      setReportEmployeeWithdrawalPermissionLoadingId(null);
    }
  };

  const handleToggleReportTopEmployeeWithdrawalPermission = async (row: Submaster) => {
    if (reportTopEmployeeWithdrawalPermissionLoadingId === row.id || !row.id) {
      return;
    }

    const nextValue = !isReportTopEmployeeWithdrawalPermissionEnabled(row);
    const actionLabel = nextValue ? "ativar" : "bloquear";
    const confirmed = window.confirm(
      `Deseja ${actionLabel} o acesso ao relatório Top 10 funcionários com maiores retiradas de EPI para a empresa ${getCompanyName(row)}?`
    );

    if (!confirmed) {
      return;
    }

    try {
      setReportTopEmployeeWithdrawalPermissionLoadingId(row.id);
      const ApiClient = apiClient();
      const response = await ApiClient.post(
        `/internal/updateReportTopEmployeeWithdrawalPermission`,
        {
          ...getInternalPayload(),
          submasterId: row.id,
          enabled: nextValue,
        }
      );

      if (!response?.data?.res) {
        throw new Error(response?.data?.message || "Não foi possível atualizar a permissão.");
      }

      setData((previous) =>
        previous.map((item) =>
          item.id === row.id
            ? { ...item, report_top_employee_withdrawal_permission_enabled: nextValue }
            : item
        )
      );
    } catch (error) {
      console.error(
        "Erro ao atualizar permissão do relatório Top 10 funcionários com maiores retiradas de EPI:",
        error
      );
      alert(
        "Não foi possível atualizar a permissão do relatório Top 10 funcionários com maiores retiradas de EPI."
      );
    } finally {
      setReportTopEmployeeWithdrawalPermissionLoadingId(null);
    }
  };

  const handleToggleReportEmployeesPermission = async (row: Submaster) => {
    if (reportEmployeesPermissionLoadingId === row.id || !row.id) {
      return;
    }

    const nextValue = !isReportEmployeesPermissionEnabled(row);
    const actionLabel = nextValue ? "ativar" : "bloquear";
    const confirmed = window.confirm(
      `Deseja ${actionLabel} o acesso ao Relatório de Funcionários para a empresa ${getCompanyName(row)}?`
    );

    if (!confirmed) {
      return;
    }

    try {
      setReportEmployeesPermissionLoadingId(row.id);
      const ApiClient = apiClient();
      const response = await ApiClient.post(`/internal/updateReportEmployeesPermission`, {
        ...getInternalPayload(),
        submasterId: row.id,
        enabled: nextValue,
      });

      if (!response?.data?.res) {
        throw new Error(response?.data?.message || "Não foi possível atualizar a permissão.");
      }

      setData((previous) =>
        previous.map((item) =>
          item.id === row.id
            ? { ...item, report_employees_permission_enabled: nextValue }
            : item
        )
      );
    } catch (error) {
      console.error("Erro ao atualizar permissão do Relatório de Funcionários:", error);
      alert("Não foi possível atualizar a permissão do Relatório de Funcionários.");
    } finally {
      setReportEmployeesPermissionLoadingId(null);
    }
  };

  const handleToggleReportExcelPermission = async (row: Submaster) => {
    if (reportExcelPermissionLoadingId === row.id || !row.id) {
      return;
    }

    const nextValue = !isReportExcelPermissionEnabled(row);
    const actionLabel = nextValue ? "ativar" : "bloquear";
    const confirmed = window.confirm(
      `Deseja ${actionLabel} a opção Baixar em Excel para a empresa ${getCompanyName(row)}?`
    );

    if (!confirmed) {
      return;
    }

    try {
      setReportExcelPermissionLoadingId(row.id);
      const ApiClient = apiClient();
      const response = await ApiClient.post(`/internal/updateReportExcelPermission`, {
        ...getInternalPayload(),
        submasterId: row.id,
        enabled: nextValue,
      });

      if (!response?.data?.res) {
        throw new Error(response?.data?.message || "Não foi possível atualizar a permissão.");
      }

      setData((previous) =>
        previous.map((item) =>
          item.id === row.id
            ? { ...item, report_excel_permission_enabled: nextValue }
            : item
        )
      );
    } catch (error) {
      console.error("Erro ao atualizar a permissão para baixar em Excel:", error);
      alert("Não foi possível atualizar a permissão para baixar em Excel.");
    } finally {
      setReportExcelPermissionLoadingId(null);
    }
  };

  const handleToggleStockControlPermission = async (row: Submaster) => {
    if (stockControlPermissionLoadingId === row.id || !row.id) {
      return;
    }

    const nextValue = !isStockControlPermissionEnabled(row);
    const actionLabel = nextValue ? "ativar" : "bloquear";
    const confirmed = window.confirm(
      `Deseja ${actionLabel} o Controle de estoque para a empresa ${getCompanyName(row)}?`
    );

    if (!confirmed) {
      return;
    }

    try {
      setStockControlPermissionLoadingId(row.id);
      const ApiClient = apiClient();
      const response = await ApiClient.post(`/internal/updateStockControlPermission`, {
        ...getInternalPayload(),
        submasterId: row.id,
        enabled: nextValue,
      });

      if (!response?.data?.res) {
        throw new Error(response?.data?.message || "Não foi possível atualizar a permissão.");
      }

      setData((previous) =>
        previous.map((item) =>
          item.id === row.id
            ? { ...item, stock_control_permission_enabled: nextValue }
            : item
        )
      );
    } catch (error) {
      console.error("Erro ao atualizar a permissão de Controle de estoque:", error);
      alert("Não foi possível atualizar a permissão de Controle de estoque.");
    } finally {
      setStockControlPermissionLoadingId(null);
    }
  };

  const handleToggleRetirarFotoPermission = async (row: Submaster) => {
    if (retirarFotoPermissionLoadingId === row.id || !row.id) {
      return;
    }

    const nextValue = !isRetirarFotoEntregaPermissionEnabled(row);
    const actionLabel = nextValue ? "ativar" : "bloquear";
    const confirmed = window.confirm(
      `Deseja ${actionLabel} a permissão "Retirar foto entrega" para a empresa ${getCompanyName(row)}?`
    );

    if (!confirmed) {
      return;
    }

    try {
      setRetirarFotoPermissionLoadingId(row.id);
      const ApiClient = apiClient();
      const response = await ApiClient.post(`/internal/updateRetirarFotoEntregaPermission`, {
        ...getInternalPayload(),
        submasterId: row.id,
        enabled: nextValue,
      });

      if (!response?.data?.res) {
        throw new Error(response?.data?.message || "Não foi possível atualizar a permissão.");
      }

      setData((previous) =>
        previous.map((item) =>
          item.id === row.id
            ? { ...item, retirar_foto_entrega_permission_enabled: nextValue }
            : item
        )
      );
    } catch (error) {
      console.error("Erro ao atualizar permissão de retirar foto entrega:", error);
      alert("Não foi possível atualizar a permissão de retirar foto entrega.");
    } finally {
      setRetirarFotoPermissionLoadingId(null);
    }
  };

  const handleToggleCameraTraseiraPermission = async (row: Submaster) => {
    if (cameraTraseiraPermissionLoadingId === row.id || !row.id) {
      return;
    }

    const nextValue = !isCameraTraseiraPermissionEnabled(row);
    const actionLabel = nextValue ? "ativar" : "bloquear";
    const confirmed = window.confirm(
      `Deseja ${actionLabel} a permissao para utilizar a camera traseira para registrar foto na empresa ${getCompanyName(row)}?`
    );

    if (!confirmed) {
      return;
    }

    try {
      setCameraTraseiraPermissionLoadingId(row.id);
      const ApiClient = apiClient();
      const response = await ApiClient.post(`/internal/updateCameraTraseiraPermission`, {
        ...getInternalPayload(),
        submasterId: row.id,
        enabled: nextValue,
      });

      if (!response?.data?.res) {
        throw new Error(response?.data?.message || "Nao foi possivel atualizar a permissao.");
      }

      setData((previous) =>
        previous.map((item) =>
          item.id === row.id
            ? { ...item, camera_traseira_permission_enabled: nextValue }
            : item
        )
      );
    } catch (error) {
      console.error("Erro ao atualizar permissao da camera traseira:", error);
      alert("Nao foi possivel atualizar a permissao da camera traseira.");
    } finally {
      setCameraTraseiraPermissionLoadingId(null);
    }
  };

  const handleToggleDeliveryTypeReasonPermission = async (row: Submaster) => {
    if (deliveryTypeReasonPermissionLoadingId === row.id) return;
    const enabled = !isDeliveryTypeReasonPermissionEnabled(row);
    try {
      setDeliveryTypeReasonPermissionLoadingId(row.id);
      const response = await apiClient().post("/internal/updateDeliveryTypeReasonPermission", {
        ...getInternalPayload(), submasterId: row.id, enabled,
      });
      if (!response?.data?.res) throw new Error(response?.data?.message || "Falha ao atualizar permissão.");
      setData((previous) => previous.map((item) => item.id === row.id
        ? { ...item, delivery_type_reason_permission_enabled: enabled } : item));
    } catch (error) {
      alert("Não foi possível atualizar a permissão de tipo e motivo de entrega.");
    } finally { setDeliveryTypeReasonPermissionLoadingId(null); }
  };

  const handleToggleEmployeeSignaturePhotoPermission = async (row: Submaster) => {
    if (employeeSignaturePhotoPermissionLoadingId === row.id || !row.id) return;
    const enabled = !isEmployeeSignaturePhotoPermissionEnabled(row);
    const confirmed = window.confirm(
      `Deseja ${enabled ? "ativar" : "bloquear"} a Foto da Assinatura do Funcionário para a empresa ${getCompanyName(row)}?`
    );
    if (!confirmed) return;

    try {
      setEmployeeSignaturePhotoPermissionLoadingId(row.id);
      const response = await apiClient().post("/internal/updateEmployeeSignaturePhotoPermission", {
        ...getInternalPayload(), submasterId: row.id, enabled,
      });
      if (!response?.data?.res) throw new Error(response?.data?.message || "Falha ao atualizar permissão.");
      setData((previous) => previous.map((item) => item.id === row.id
        ? { ...item, employee_signature_photo_permission_enabled: enabled } : item));
    } catch (error) {
      console.error("Erro ao atualizar permissão da foto da assinatura:", error);
      alert("Não foi possível atualizar a permissão da foto da assinatura do funcionário.");
    } finally {
      setEmployeeSignaturePhotoPermissionLoadingId(null);
    }
  };

  const handleToggleFixedReportSignaturePermission = async (row: Submaster) => {
    if (fixedReportSignaturePermissionLoadingId === row.id || !row.id) return;
    const enabled = !isFixedReportSignaturePermissionEnabled(row);
    if (!window.confirm(`Deseja ${enabled ? "ativar" : "bloquear"} a assinatura fixa nos relatórios para a empresa ${getCompanyName(row)}?`)) return;

    try {
      setFixedReportSignaturePermissionLoadingId(row.id);
      const response = await apiClient().post("/internal/updateFixedReportSignaturePermission", {
        ...getInternalPayload(), submasterId: row.id, enabled,
      });
      if (!response?.data?.res) throw new Error(response?.data?.message || "Falha ao atualizar permissão.");
      setData((previous) => previous.map((item) => item.id === row.id
        ? { ...item, fixed_report_signature_permission_enabled: enabled } : item));
    } catch (error) {
      console.error("Erro ao atualizar permissão da assinatura fixa:", error);
      alert("Não foi possível atualizar a permissão da assinatura fixa.");
    } finally {
      setFixedReportSignaturePermissionLoadingId(null);
    }
  };

  const handleDeleteSubmaster = async (row: Submaster) => {
    if (deleteLoadingId === row.id || !row.id) {
      return;
    }

    const confirmed = window.confirm(
      `Atenção: todos os dados da empresa ${getCompanyName(row)} serão apagados permanentemente.\n\n` +
        `CNPJ: ${formatCNPJ(row.cnpj)}\n` +
        "Isso remove funcionários, usuários, EPIs, documentos, empresas vinculadas e arquivos.\n\n" +
        "Deseja continuar?"
    );

    if (!confirmed) {
      return;
    }

    try {
      setDeleteLoadingId(row.id);
      const ApiClient = apiClient();
      const response = await ApiClient.post(`/internal/deleteSubmaster`, {
        ...getInternalPayload(),
        submasterId: row.id,
      });

      if (!response?.data?.res) {
        throw new Error(response?.data?.message || "Não foi possível excluir a empresa.");
      }

      setData((previous) => previous.filter((item) => item.id !== row.id));
      setExpandedCompanyId((currentId) => (currentId === row.id ? null : currentId));
      alert("Empresa excluída com sucesso.");
    } catch (error) {
      console.error("Erro ao excluir empresa:", error);
      alert("Não foi possível excluir a empresa.");
    } finally {
      setDeleteLoadingId(null);
    }
  };

  const columns: TableColumn<Submaster>[] = [
      {
        name: "Empresa",
        selector: (row) => getCompanyName(row),
        sortable: true,
        grow: 2,
      },
      {
        name: "CNPJ",
        selector: (row) => formatCNPJ(row.cnpj),
        sortable: true,
        grow: 1.8,
      },
      {
        name: "Acessar Empresa",
        cell: (row) => (
          <button
            type="button"
            className="btn btn-sm btn-primary d-inline-flex align-items-center justify-content-center"
            disabled={accessCompanyLoadingId !== null}
            onClick={(event) => {
              event.stopPropagation();
              handleAccessCompany(row);
            }}
            style={{ whiteSpace: "nowrap", minWidth: "150px", minHeight: "36px" }}
          >
            {accessCompanyLoadingId === row.id ? (
              <span className="spinner-border spinner-border-sm me-2" role="status" />
            ) : (
              <i className="bi bi-box-arrow-in-right me-2"></i>
            )}
            {accessCompanyLoadingId === row.id ? "Acessando..." : "Acessar Empresa"}
          </button>
        ),
        minWidth: "190px",
        grow: 0,
      },
      {
        name: "Email",
        selector: (row) => row.email || "—",
        sortable: true,
        grow: 2.5,
      },
      {
        name: "Regra Geral",
        cell: (row) => {
          const enabled = isDeletePermissionEnabled(row);
          const isBusy = permissionLoadingId === row.id;
          const emailEnabled = isEmailPermissionEnabled(row);
          const isEmailBusy = emailPermissionLoadingId === row.id;
          const reportLowStockEnabled = isReportLowStockPermissionEnabled(row);
          const isReportLowStockBusy = reportLowStockPermissionLoadingId === row.id;
          const reportEpiRetiradaEnabled = isReportEpiRetiradaPermissionEnabled(row);
          const isReportEpiRetiradaBusy = reportEpiRetiradaPermissionLoadingId === row.id;
          const retirarFotoEnabled = isRetirarFotoEntregaPermissionEnabled(row);
          const isRetirarFotoBusy = retirarFotoPermissionLoadingId === row.id;

          return (
            <div className="d-flex flex-column gap-2 py-2" style={{ minWidth: 220 }}>
              <span
                className={`badge ${enabled ? "bg-success-subtle text-success" : "bg-secondary-subtle text-secondary"}`}
                style={{
                  border: "1px solid rgba(13, 110, 253, 0.08)",
                  padding: "8px 10px",
                  borderRadius: "999px",
                }}
              >
                Permissão de exclusão {enabled ? "ativa" : "bloqueada"}
              </span>

              <div className="d-flex align-items-center justify-content-between gap-2">
                <small style={{ color: "#5f738a", fontWeight: 600 }}>Permissão de exclusão</small>
                <div className="form-check form-switch m-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={enabled}
                    disabled={isBusy}
                    onChange={() => handleToggleDeletePermission(row)}
                  />
                </div>
              </div>

              <span
                className={`badge ${emailEnabled ? "bg-success-subtle text-success" : "bg-secondary-subtle text-secondary"}`}
                style={{
                  border: "1px solid rgba(13, 110, 253, 0.08)",
                  padding: "8px 10px",
                  borderRadius: "999px",
                }}
              >
                Permissão de e-mail {emailEnabled ? "ativa" : "bloqueada"}
              </span>

              <div className="d-flex align-items-center justify-content-between gap-2">
                <small style={{ color: "#5f738a", fontWeight: 600 }}>Permissão de e-mail</small>
                <div className="form-check form-switch m-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={emailEnabled}
                    disabled={isEmailBusy}
                    onChange={() => handleToggleEmailPermission(row)}
                  />
                </div>
              </div>

              <span
                className={`badge ${reportLowStockEnabled ? "bg-success-subtle text-success" : "bg-secondary-subtle text-secondary"}`}
                style={{
                  border: "1px solid rgba(13, 110, 253, 0.08)",
                  padding: "8px 10px",
                  borderRadius: "999px",
                }}
              >
                Relatório de EPI com estoque baixo {reportLowStockEnabled ? "liberado" : "bloqueado"}
              </span>

              <div className="d-flex align-items-center justify-content-between gap-2">
                <small style={{ color: "#5f738a", fontWeight: 600 }}>Acesso ao relatório de todos EPI com estoque baixo</small>
                <div className="form-check form-switch m-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={reportLowStockEnabled}
                    disabled={isReportLowStockBusy}
                    onChange={() => handleToggleReportLowStockPermission(row)}
                  />
                </div>
              </div>

              <span
                className={`badge ${reportEpiRetiradaEnabled ? "bg-success-subtle text-success" : "bg-secondary-subtle text-secondary"}`}
                style={{
                  border: "1px solid rgba(13, 110, 253, 0.08)",
                  padding: "8px 10px",
                  borderRadius: "999px",
                }}
              >
                Relatório de retirada de EPI {reportEpiRetiradaEnabled ? "liberado" : "bloqueado"}
              </span>

              <div className="d-flex align-items-center justify-content-between gap-2">
                <small style={{ color: "#5f738a", fontWeight: 600 }}>Relatório de retirada de EPI</small>
                <div className="form-check form-switch m-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={reportEpiRetiradaEnabled}
                    disabled={isReportEpiRetiradaBusy}
                    onChange={() => handleToggleReportEpiRetiradaPermission(row)}
                  />
                </div>
              </div>

              <span
                className={`badge ${retirarFotoEnabled ? "bg-success-subtle text-success" : "bg-secondary-subtle text-secondary"}`}
                style={{
                  border: "1px solid rgba(13, 110, 253, 0.08)",
                  padding: "8px 10px",
                  borderRadius: "999px",
                }}
              >
                Retirar foto entrega {retirarFotoEnabled ? "liberado" : "bloqueado"}
              </span>

              <div className="d-flex align-items-center justify-content-between gap-2">
                <small style={{ color: "#5f738a", fontWeight: 600 }}>Retirar foto entrega</small>
                <div className="form-check form-switch m-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={retirarFotoEnabled}
                    disabled={isRetirarFotoBusy}
                    onChange={() => handleToggleRetirarFotoPermission(row)}
                  />
                </div>
              </div>
            </div>
          );
        },
        minWidth: "260px",
        grow: 1.8,
      },
      {
        name: "Atualizado em",
        selector: (row) => formatDate(row.updatedAt),
        sortable: true,
        grow: 1.5,
      },
      {
        name: "Ações",
        cell: (row) => (
          <div className="d-flex flex-column gap-2 py-2" style={{ width: "100%" }}>
            <button
              type="button"
              className="btn btn-sm btn-outline-primary d-inline-flex align-items-center justify-content-center"
              onClick={() => copyCNPJ(row.cnpj)}
              style={{
                whiteSpace: "nowrap",
                width: "100%",
                minHeight: "34px",
                padding: "6px 12px",
              }}
            >
              <i className="bi bi-clipboard me-1"></i>
              Copiar CNPJ
            </button>

            <button
              type="button"
              className="btn btn-sm btn-outline-danger d-inline-flex align-items-center justify-content-center"
              onClick={() => handleDeleteSubmaster(row)}
              disabled={deleteLoadingId === row.id}
              style={{
                whiteSpace: "nowrap",
                width: "100%",
                minHeight: "34px",
                padding: "6px 12px",
              }}
            >
              <i className="bi bi-trash me-1"></i>
              {deleteLoadingId === row.id ? "Excluindo..." : "Excluir empresa"}
            </button>
          </div>
        ),
        minWidth: "190px",
        grow: 0,
      },
    ];

  const infoCards = useMemo(
    () => [
      { label: "Empresas cadastradas", value: data.length, icon: "bi-buildings" },
      { label: "Resultados filtrados", value: filteredData.length, icon: "bi-funnel" },
    ],
    [data, filteredData.length]
  );

  const companyColumns = columns.slice(0, 3);

  const ExpandedCompanyRules = ({ data: row }: { data: Submaster }) => {
    const rules = [
      {
        key: "delete",
        title: "Permissão de exclusão",
        description: "Permite excluir permanentemente dados nos relatórios da empresa.",
        enabled: isDeletePermissionEnabled(row),
        busy: permissionLoadingId === row.id,
        icon: "bi-trash3",
        onToggle: () => handleToggleDeletePermission(row),
      },
      {
        key: "master-data-delete",
        title: "Excluir funcionário, usuário e EPI",
        description: "Essa permissão permite excluir funcionário, usuário e EPI que não foi utilizado em nenhuma lista.",
        enabled: isMasterDataDeletePermissionEnabled(row),
        busy: masterDataDeletePermissionLoadingId === row.id,
        icon: "bi-person-x",
        onToggle: () => handleToggleMasterDataDeletePermission(row),
      },
      {
        key: "company-delete",
        title: "Permissão de Exclusão de Empresa",
        description: "Permissão para excluir permanentemente uma empresa cadastrada na tela de cadastro de empresas.",
        enabled: isCompanyDeletePermissionEnabled(row),
        busy: companyDeletePermissionLoadingId === row.id,
        icon: "bi-building-x",
        onToggle: () => handleToggleCompanyDeletePermission(row),
      },
      {
        key: "email",
        title: "Envio de e-mails de alerta",
        description: "Envia alertas de EPI vencido e estoque mínimo.",
        enabled: isEmailPermissionEnabled(row),
        busy: emailPermissionLoadingId === row.id,
        icon: "bi-envelope",
        onToggle: () => handleToggleEmailPermission(row),
      },
      {
        key: "individual-photo-by-epi",
        title: "Relatório Individual com Foto por EPI",
        description: "Relatório Individual com Foto por EPI.",
        enabled: isBaseReportPermissionEnabled(row, "individual-photo-by-epi"),
        busy: baseReportPermissionLoadingKey === `${row.id}:individual-photo-by-epi`,
        icon: "bi-file-earmark-image",
        onToggle: () =>
          handleToggleBaseReportPermission(
            row,
            "individual-photo-by-epi",
            "Relatório Individual com Foto por EPI"
          ),
      },
      {
        key: "epi-report",
        title: "Relatório de EPIs",
        description: "Dados cadastrais e posição atual de estoque dos EPIs.",
        enabled: isBaseReportPermissionEnabled(row, "epi-report"),
        busy: baseReportPermissionLoadingKey === `${row.id}:epi-report`,
        icon: "bi-shield-check",
        onToggle: () =>
          handleToggleBaseReportPermission(row, "epi-report", "Relatório de EPIs"),
      },
      {
        key: "top-largest",
        title: "Top 10 maiores retiradas de EPI",
        description: "EPIs com maior volume total de retirada.",
        enabled: isBaseReportPermissionEnabled(row, "top-largest"),
        busy: baseReportPermissionLoadingKey === `${row.id}:top-largest`,
        icon: "bi-graph-up-arrow",
        onToggle: () =>
          handleToggleBaseReportPermission(
            row,
            "top-largest",
            "Top 10 maiores retiradas de EPI"
          ),
      },
      {
        key: "top-smallest",
        title: "Top 10 menores retiradas de EPI",
        description: "EPIs com menor volume total de retirada.",
        enabled: isBaseReportPermissionEnabled(row, "top-smallest"),
        busy: baseReportPermissionLoadingKey === `${row.id}:top-smallest`,
        icon: "bi-graph-down-arrow",
        onToggle: () =>
          handleToggleBaseReportPermission(
            row,
            "top-smallest",
            "Top 10 menores retiradas de EPI"
          ),
      },
      {
        key: "latest-by-employee",
        title: "Última retirada por funcionário",
        description: "Data da última retirada de EPI por funcionário.",
        enabled: isBaseReportPermissionEnabled(row, "latest-by-employee"),
        busy: baseReportPermissionLoadingKey === `${row.id}:latest-by-employee`,
        icon: "bi-file-earmark-person",
        onToggle: () =>
          handleToggleBaseReportPermission(
            row,
            "latest-by-employee",
            "Relatório de última retirada por funcionário"
          ),
      },
      {
        key: "low-stock",
        title: "Relatório de EPI com estoque baixo",
        description: "Libera o relatório com todos os EPIs abaixo do estoque mínimo.",
        enabled: isReportLowStockPermissionEnabled(row),
        busy: reportLowStockPermissionLoadingId === row.id,
        icon: "bi-clipboard-data",
        onToggle: () => handleToggleReportLowStockPermission(row),
      },
      {
        key: "epi-output-report",
        title: "Relatório de Saída de EPIs",
        description: "Soma de todas as retiradas de EPIs por período.",
        enabled: isEpiOutputReportPermissionEnabled(row),
        busy: epiOutputReportPermissionLoadingId === row.id,
        icon: "bi-box-arrow-up-right",
        onToggle: () => handleToggleEpiOutputReportPermission(row),
      },
      {
        key: "epi-output-cost-report",
        title: "Custo de Saída de EPI por Empresa",
        description: "Custo de Saída de EPI por Empresa",
        enabled: isBaseReportPermissionEnabled(row, "epi-output-cost-report"),
        busy: baseReportPermissionLoadingKey === `${row.id}:epi-output-cost-report`,
        icon: "bi-cash-stack",
        onToggle: () =>
          handleToggleBaseReportPermission(
            row,
            "epi-output-cost-report",
            "Custo de Saída de EPI por Empresa"
          ),
      },
      {
        key: "epi-output-employee-cost-report",
        title: "Custo de Saída de EPI por Empresa e Funcionário",
        description: "Custo de Saída de EPI por Empresa e Funcionário",
        enabled: isBaseReportPermissionEnabled(row, "epi-output-employee-cost-report"),
        busy:
          baseReportPermissionLoadingKey === `${row.id}:epi-output-employee-cost-report`,
        icon: "bi-people-fill",
        onToggle: () =>
          handleToggleBaseReportPermission(
            row,
            "epi-output-employee-cost-report",
            "Custo de Saída de EPI por Empresa e Funcionário"
          ),
      },
      {
        key: "epi-output-grouped-employee-cost-report",
        title: "Custo de Saída de EPI Agrupado por Funcionário",
        description: "Custo de Saída de EPI Agrupado por Funcionário",
        enabled: isBaseReportPermissionEnabled(
          row,
          "epi-output-grouped-employee-cost-report"
        ),
        busy:
          baseReportPermissionLoadingKey ===
          `${row.id}:epi-output-grouped-employee-cost-report`,
        icon: "bi-collection-fill",
        onToggle: () =>
          handleToggleBaseReportPermission(
            row,
            "epi-output-grouped-employee-cost-report",
            "Custo de Saída de EPI Agrupado por Funcionário"
          ),
      },
      {
        key: "epi-withdrawal",
        title: "Relatório de retirada de EPI",
        description: "Libera o acesso ao relatório de retirada de EPI.",
        enabled: isReportEpiRetiradaPermissionEnabled(row),
        busy: reportEpiRetiradaPermissionLoadingId === row.id,
        icon: "bi-box-arrow-up",
        onToggle: () => handleToggleReportEpiRetiradaPermission(row),
      },
      {
        key: "employee-epi-withdrawal",
        title: "Relatório de Retirada de EPI por funcionário",
        description: "Liberar acesso a Relatório de Retirada de EPI por funcionário",
        enabled: isReportEmployeeWithdrawalPermissionEnabled(row),
        busy: reportEmployeeWithdrawalPermissionLoadingId === row.id,
        icon: "bi-person-lines-fill",
        onToggle: () => handleToggleReportEmployeeWithdrawalPermission(row),
      },
      {
        key: "cost-by-employee",
        title: "Custo de EPI por funcionário",
        description: "Custo de EPI por funcionário",
        enabled: isBaseReportPermissionEnabled(row, "cost-by-employee"),
        busy: baseReportPermissionLoadingKey === `${row.id}:cost-by-employee`,
        icon: "bi-currency-dollar",
        onToggle: () =>
          handleToggleBaseReportPermission(
            row,
            "cost-by-employee",
            "Custo de EPI por funcionário"
          ),
      },
      {
        key: "top-employee-epi-withdrawals",
        title: "Top 10 funcionários com maiores retiradas de EPI",
        description: "Top 10 funcionários que mais retiraram EPI.",
        enabled: isReportTopEmployeeWithdrawalPermissionEnabled(row),
        busy: reportTopEmployeeWithdrawalPermissionLoadingId === row.id,
        icon: "bi-people-fill",
        onToggle: () => handleToggleReportTopEmployeeWithdrawalPermission(row),
      },
      {
        key: "employees-report",
        title: "Relatório de Funcionários",
        description: "Lista dos funcionários.",
        enabled: isReportEmployeesPermissionEnabled(row),
        busy: reportEmployeesPermissionLoadingId === row.id,
        icon: "bi-person-vcard",
        onToggle: () => handleToggleReportEmployeesPermission(row),
      },
      {
        key: "report-excel",
        title: "Baixar em Excel",
        description: "Permitir extrair relatórios em Excel.",
        enabled: isReportExcelPermissionEnabled(row),
        busy: reportExcelPermissionLoadingId === row.id,
        icon: "bi-file-earmark-excel",
        onToggle: () => handleToggleReportExcelPermission(row),
      },
      {
        key: "stock-control",
        title: "Controla Estoque",
        description: "Habilita entradas e devoluções de estoque.",
        enabled: isStockControlPermissionEnabled(row),
        busy: stockControlPermissionLoadingId === row.id,
        icon: "bi-box-seam",
        onToggle: () => handleToggleStockControlPermission(row),
      },
      {
        key: "rear-camera",
        title: "Utilizar câmera traseira para registrar foto",
        description: "Possibilita tirar foto da câmera frontal ou traseira.",
        enabled: isCameraTraseiraPermissionEnabled(row),
        busy: cameraTraseiraPermissionLoadingId === row.id,
        icon: "bi-arrow-repeat",
        onToggle: () => handleToggleCameraTraseiraPermission(row),
      },
      {
        key: "delivery-photo",
        title: "Retirar foto da entrega",
        description: "Permite concluir a entrega sem exigir a foto.",
        enabled: isRetirarFotoEntregaPermissionEnabled(row),
        busy: retirarFotoPermissionLoadingId === row.id,
        icon: "bi-camera",
        onToggle: () => handleToggleRetirarFotoPermission(row),
      },
      {
        key: "employee-signature-photo",
        title: "Foto da Assinatura do Funcionário",
        description: "Liberar acesso a registrar foto da assinatura do funcionário.",
        enabled: isEmployeeSignaturePhotoPermissionEnabled(row),
        busy: employeeSignaturePhotoPermissionLoadingId === row.id,
        icon: "bi-camera-fill",
        onToggle: () => handleToggleEmployeeSignaturePhotoPermission(row),
      },
      {
        key: "fixed-report-signature",
        title: "Assinatura do relatório de Conjunto",
        description: "Assinatura que ficará fixa no relatório de entrega de EPI por funcionário e relatório de conjunto.",
        enabled: isFixedReportSignaturePermissionEnabled(row),
        busy: fixedReportSignaturePermissionLoadingId === row.id,
        icon: "bi-pen",
        onToggle: () => handleToggleFixedReportSignaturePermission(row),
      },
      {
        key: "delivery-type-reason",
        title: "Informar Tipo e Motivo de entrega de EPI",
        description: "Ao marcar essa permissão, habilita que seja informado o tipo e o motivo da entrega do EPI no app.",
        enabled: isDeliveryTypeReasonPermissionEnabled(row),
        busy: deliveryTypeReasonPermissionLoadingId === row.id,
        icon: "bi-list-check",
        onToggle: () => handleToggleDeliveryTypeReasonPermission(row),
      },
    ];

    return (
      <div
        className="p-3 p-lg-4"
        style={{
          background: "#f7faff",
          borderTop: "1px solid rgba(13,110,253,0.1)",
          borderLeft: "4px solid #0d6efd",
        }}
      >
        <div className="d-flex flex-column flex-lg-row justify-content-between gap-3 mb-3">
          <div>
            <div className="d-flex align-items-center gap-2 mb-1">
              <i className="bi bi-folder2-open text-primary"></i>
              <h3 className="h5 fw-bold mb-0" style={{ color: "#17324d" }}>
                Regras gerais e permissões
              </h3>
            </div>
            <p className="mb-0" style={{ color: "#6d8198" }}>
              Configurações aplicadas somente à empresa {getCompanyName(row)}.
            </p>
          </div>

          <div className="text-lg-end" style={{ color: "#6d8198", fontSize: "0.86rem" }}>
            <div>{row.email || "E-mail não informado"}</div>
            <div>Atualizado em {formatDate(row.updatedAt)}</div>
          </div>
        </div>

        <div className="row g-3">
          {rules.map((rule) => (
            <div className="col-12 col-xl-6" key={rule.key}>
              <div
                className="h-100 d-flex align-items-center justify-content-between gap-3"
                style={{
                  padding: "16px",
                  borderRadius: "16px",
                  background: "#fff",
                  border: "1px solid rgba(18,49,58,0.09)",
                }}
              >
                <div className="d-flex align-items-start gap-3">
                  <span
                    className="d-flex align-items-center justify-content-center flex-shrink-0"
                    style={{
                      width: "42px",
                      height: "42px",
                      borderRadius: "12px",
                      color: rule.enabled ? "#198754" : "#6c757d",
                      background: rule.enabled ? "rgba(25,135,84,0.1)" : "rgba(108,117,125,0.1)",
                    }}
                  >
                    <i className={`bi ${rule.icon}`}></i>
                  </span>
                  <div>
                    <div className="fw-bold mb-1" style={{ color: "#29445c" }}>
                      {rule.title}
                    </div>
                    <div style={{ color: "#6d8198", fontSize: "0.84rem" }}>
                      {rule.description}
                    </div>
                    <span
                      className={`badge mt-2 ${rule.enabled ? "bg-success-subtle text-success" : "bg-secondary-subtle text-secondary"}`}
                    >
                      {rule.enabled ? "Ativa" : "Bloqueada"}
                    </span>
                  </div>
                </div>

                <div className="form-check form-switch m-0 flex-shrink-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    aria-label={`${rule.title}: ${rule.enabled ? "ativa" : "bloqueada"}`}
                    checked={rule.enabled}
                    disabled={rule.busy}
                    onChange={rule.onToggle}
                    style={{ width: "2.7rem", height: "1.35rem", cursor: rule.busy ? "wait" : "pointer" }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        <div
          className="d-flex flex-column flex-md-row justify-content-md-end align-items-md-center gap-3 mt-4 pt-3"
          style={{ borderTop: "1px solid rgba(18,49,58,0.09)" }}
        >
          <button
            type="button"
            className="btn btn-sm btn-outline-danger"
            onClick={() => handleDeleteSubmaster(row)}
            disabled={deleteLoadingId === row.id}
          >
            <i className="bi bi-trash me-2"></i>
            {deleteLoadingId === row.id ? "Excluindo..." : "Excluir empresa e todos os dados"}
          </button>
        </div>
        <WebUserManagementPanel submasterId={row.id} companyName={getCompanyName(row)} />
      </div>
    );
  };

  return (
    <div
      className="d-flex flex-column justify-content-center align-items-center"
      style={{
        minHeight: "100vh",
        padding: "24px",
        background:
          "radial-gradient(circle at top left, rgba(13,110,253,0.18), transparent 28%), linear-gradient(135deg, #eef5ff 0%, #f7fbff 48%, #eef4ff 100%)",
      }}
    >
      {!authenticated ? (
        <div
          className="shadow-lg"
          style={{
            borderRadius: "28px",
            maxWidth: "460px",
            width: "100%",
            padding: "32px",
            background: "rgba(255,255,255,0.96)",
            border: "1px solid rgba(13,110,253,0.08)",
            boxShadow: "0 30px 60px rgba(28, 63, 110, 0.12)",
          }}
        >
          <div
            className="d-inline-flex align-items-center justify-content-center mb-3"
            style={{
              width: "68px",
              height: "68px",
              borderRadius: "20px",
              background: "linear-gradient(135deg, #0d6efd, #67a6ff)",
              color: "#fff",
              fontSize: "1.9rem",
            }}
          >
            <i className="bi bi-shield-lock-fill"></i>
          </div>
          <div className="mb-4">
            <span
              className="d-inline-block mb-2"
              style={{
                fontSize: "0.78rem",
                fontWeight: 700,
                letterSpacing: "0.08em",
                color: "#4d79a6",
                textTransform: "uppercase",
              }}
            >
              Acesso interno
            </span>
            <h2 className="fw-bold mb-2" style={{ color: "#17324d" }}>
              Chave de acesso
            </h2>
            <p className="mb-0" style={{ color: "#6d8198" }}>
              Informe a chave apenas uma vez. Depois disso, o acesso fica salvo
              neste navegador até você sair.
            </p>
          </div>

          <div className="input-group mb-3">
            <span className="input-group-text bg-white border-end-0">
              <i className="bi bi-key-fill text-primary"></i>
            </span>
            <input
              type="password"
              className="form-control border-start-0"
              placeholder="Digite a chave de acesso"
              value={accessKey}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setAccessKey(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleAccess();
                }
              }}
              style={{ padding: "12px 14px" }}
            />
          </div>

          <button className="btn btn-primary w-100 py-2 fw-semibold" onClick={handleAccess}>
            Entrar
          </button>
        </div>
      ) : (
        <div
          className="shadow-lg"
          style={{
            width: "100%",
            maxWidth: "1420px",
            borderRadius: "28px",
            padding: "28px",
            background: "rgba(255,255,255,0.96)",
            border: "1px solid rgba(13,110,253,0.08)",
            boxShadow: "0 30px 60px rgba(28, 63, 110, 0.12)",
          }}
        >
          <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3 mb-4">
            <div>
              <span
                className="d-inline-block mb-2"
                style={{
                  fontSize: "0.78rem",
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  color: "#4d79a6",
                  textTransform: "uppercase",
                }}
              >
                Configuração interna
              </span>
              <h2 className="fw-bold mb-1" style={{ color: "#17324d" }}>
                Configuração
              </h2>
              <p className="mb-0" style={{ color: "#6d8198" }}>
                Selecione uma empresa para abrir as regras gerais e suas permissões.
              </p>
            </div>

            <button className="btn btn-outline-danger align-self-start" onClick={handleLogout}>
              <i className="bi bi-box-arrow-right me-2"></i>
              Sair
            </button>
          </div>

          <DeliveryOptionsTable />

          <div className="row g-3 mb-4">
            {infoCards.map((card) => (
              <div className="col-12 col-md-6" key={card.label}>
                <div
                  className="h-100 w-100"
                  style={{
                    borderRadius: "20px",
                    padding: "18px 20px",
                    background: "#f8fbff",
                    border: "1px solid rgba(13,110,253,0.08)",
                    boxShadow: "0 12px 24px rgba(28, 63, 110, 0.06)",
                  }}
                >
                  <div className="d-flex align-items-center gap-3 flex-wrap">
                    <div
                      className="d-flex align-items-center justify-content-center"
                      style={{
                        width: "46px",
                        height: "46px",
                        borderRadius: "14px",
                        background: "rgba(13,110,253,0.1)",
                        color: "#0d6efd",
                        fontSize: "1.1rem",
                      }}
                    >
                      <i className={`bi ${card.icon}`}></i>
                    </div>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontSize: "0.82rem", color: "#6d8198" }}>{card.label}</div>
                      <div style={{ fontSize: "1.2rem", fontWeight: 700, color: "#17324d" }}>
                        {card.value}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div
            className="mb-4"
            style={{
              borderRadius: "22px",
              padding: "20px",
              background: "#f8fbff",
              border: "1px solid rgba(13,110,253,0.08)",
            }}
          >
            <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3 mb-3">
              <div>
                <h3 className="h5 fw-bold mb-1" style={{ color: "#17324d" }}>
                  Localizar empresa
                </h3>
                <p className="mb-0" style={{ color: "#6d8198" }}>
                  Pesquise pelo nome da empresa ou pelo CNPJ.
                </p>
              </div>

              <button className="btn btn-outline-secondary" onClick={clearFilters}>
                <i className="bi bi-arrow-counterclockwise me-2"></i>
                Limpar filtros
              </button>
            </div>

            <div className="row g-3">
              <div className="col-12 col-md-6">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Pesquisar por nome da empresa"
                  value={filterCompany}
                  onChange={(e) => setFilterCompany(e.target.value)}
                  style={{ minHeight: "42px" }}
                />
              </div>
              <div className="col-12 col-md-6">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Pesquisar por CNPJ"
                  value={filterCNPJ}
                  onChange={(e) => setFilterCNPJ(e.target.value)}
                  style={{ minHeight: "42px" }}
                />
              </div>
            </div>
          </div>

          <div
            className="alert alert-primary mb-4 d-flex align-items-center gap-2"
            style={{
              borderRadius: "18px",
              border: "1px solid rgba(13, 110, 253, 0.18)",
              background: "rgba(231, 241, 255, 0.78)",
            }}
          >
            <i className="bi bi-info-circle-fill"></i>
            Clique em uma empresa para abrir as regras gerais e permissões logo abaixo dela.
          </div>

          <div
            style={{
              overflow: "hidden",
              borderRadius: "22px",
              border: "1px solid rgba(18,49,58,0.08)",
              background: "#fff",
            }}
          >
            <DataTable
              columns={companyColumns}
              data={filteredData}
              progressPending={loading}
              pagination
              paginationPerPage={15}
              paginationRowsPerPageOptions={[15, 30, 50]}
              highlightOnHover
              pointerOnHover
              responsive
              customStyles={dataTableStyles}
              expandableRows
              expandOnRowClicked
              expandableRowsComponent={ExpandedCompanyRules}
              expandableRowExpanded={(row) => row.id === expandedCompanyId}
              onRowExpandToggled={(expanded, row) =>
                setExpandedCompanyId(expanded ? row.id : null)
              }
              noDataComponent="Nenhuma empresa encontrada com os filtros aplicados."
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default DataSubmasterPage;
